import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Icons, Cascade, type IconComp } from "../lib/icons";
import { useProvider } from "../lib/hooks";
import { useProviderSwitcher } from "../lib/useProviderSwitcher";

function initialsOfOrg(name: string | undefined): string {
  if (!name) return "··";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const second = parts[1]?.[0] ?? "";
  return (first + second).toUpperCase();
}

type NavItem = {
  id: string;
  label: string;
  to: string;
  I: IconComp;
  badge?: string;
};

// Quick start has no entry of its own: its checklist sits on Overview until
// it is complete (the /quickstart route still works).
const NAV: Array<{ section: string | null; items: NavItem[] }> = [
  {
    section: null,
    items: [{ id: "dashboard", label: "Overview", to: "/", I: Icons.Home }],
  },
  {
    section: "Set up",
    items: [
      { id: "policy", label: "How people sign in", to: "/policy", I: Icons.Shield },
      { id: "signin-page", label: "Sign-in page", to: "/sign-in-page", I: Icons.Window },
      { id: "keys", label: "API keys", to: "/keys", I: Icons.Key },
    ],
  },
  {
    section: "Monitor",
    items: [
      { id: "logs", label: "Activity", to: "/logs", I: Icons.List },
      { id: "users", label: "People", to: "/users", I: Icons.Users },
    ],
  },
  {
    section: "Account",
    items: [
      { id: "team", label: "Team", to: "/team", I: Icons.Team },
      { id: "billing", label: "Billing", to: "/billing", I: Icons.Card },
      { id: "settings", label: "Settings", to: "/settings", I: Icons.Cog },
    ],
  },
];

export function Sidebar() {
  const { data: provider } = useProvider();
  const switcher = useProviderSwitcher();
  const initials = initialsOfOrg(provider?.name);
  // Truncated UUID for the "id" tag — first 8 chars is enough for ops to
  // disambiguate two orgs with the same name in a screenshot without
  // taking up the whole side bar.
  const shortId = provider?.id ? provider.id.slice(0, 8) : "–";

  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const orgMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!orgMenuOpen) return;
    function onClickAway(e: MouseEvent) {
      if (!orgMenuRef.current?.contains(e.target as Node)) setOrgMenuOpen(false);
    }
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [orgMenuOpen]);

  const orgInner = (
    <>
      <div className="ad-side-org-mono">{initials}</div>
      <div className="ad-side-org-meta">
        <div className="ad-side-org-name">{provider?.name ?? "Loading…"}</div>
        <div className="ad-side-org-id">{shortId}</div>
      </div>
    </>
  );

  return (
    <aside className="ad-side">
      <div className="ad-side-brand">
        <Cascade size={32} />
        <div className="ad-side-brand-text">
          <div className="ad-side-brand-name">
            Authen<em>See</em>
          </div>
          <div className="ad-side-brand-tag">Console</div>
        </div>
      </div>

      {/* Always a menu — even with a single org — so an operator can reach
          "Create organization" (the backend allows N orgs per account). With
          >1 org the list also acts as the switcher. */}
      <div ref={orgMenuRef} style={{ position: "relative", zIndex: orgMenuOpen ? 50 : undefined }}>
        <button
          type="button"
          className="ad-side-org"
          aria-haspopup="menu"
          aria-expanded={orgMenuOpen}
          onClick={() => setOrgMenuOpen((v) => !v)}
          disabled={switcher.isSwitching}
          style={{
            width: "100%",
            border: 0,
            background: "transparent",
            cursor: "pointer",
            textAlign: "left",
            font: "inherit",
          }}
        >
          {orgInner}
          <Icons.ChevronD size={14} className="ad-side-org-caret" />
        </button>
        {orgMenuOpen && (
          <div
            role="menu"
            className="ad-menu-pop"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              padding: 6,
              color: "var(--ad-fg-1)",
              border: "1px solid var(--ad-border-1)",
              borderRadius: 14,
              boxShadow: "var(--ad-card-shadow)",
              zIndex: 100,
            }}
          >
            {switcher.providers.map((p) => {
              const isCurrent = p.id === switcher.activeProviderId;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="menuitem"
                  aria-current={isCurrent || undefined}
                  className={`ad-menu-item${isCurrent ? " is-current" : ""}`}
                  onClick={() => {
                    setOrgMenuOpen(false);
                    switcher.switchTo(p.id);
                  }}
                >
                  <span className="ad-menu-item-body">
                    <span className="ad-menu-item-name">{p.name}</span>
                    <span className="ad-menu-item-sub" style={{ display: "block" }}>
                      {p.role}
                    </span>
                  </span>
                  {isCurrent && (
                    <Icons.Check size={14} className="ad-menu-item-check" />
                  )}
                </button>
              );
            })}
            <div style={{ height: 1, background: "var(--ad-hairline)", margin: "4px 0" }} />
            <NavLink
              to="/settings"
              role="menuitem"
              onClick={() => setOrgMenuOpen(false)}
              style={{
                display: "block",
                padding: "8px 10px",
                color: "var(--ad-fg-2)",
                borderRadius: 6,
                fontSize: 13,
                textDecoration: "none",
              }}
            >
              Organization settings
            </NavLink>
            <NavLink
              to="/organizations/new"
              role="menuitem"
              onClick={() => setOrgMenuOpen(false)}
              style={{
                display: "block",
                padding: "8px 10px",
                color: "var(--ad-fg-2)",
                borderRadius: 6,
                fontSize: 13,
                textDecoration: "none",
              }}
            >
              + Create organization
            </NavLink>
          </div>
        )}
      </div>

      {NAV.map((sec) => (
        <div key={sec.section ?? "top"}>
          {sec.section && <div className="ad-side-section">{sec.section}</div>}
          {sec.items.map((it) => (
            <NavLink
              key={it.id}
              to={it.to}
              end={it.to === "/"}
              onClick={() => {
                // Close the off-canvas drawer once a destination is picked
                // on mobile. No-op on desktop.
                document.body.removeAttribute("data-mobile-nav-open");
              }}
              className={({ isActive }) =>
                `ad-side-item${isActive ? " is-active" : ""}`
              }
            >
              <it.I size={16} className="ad-side-item-icon" />
              <span>{it.label}</span>
              {it.badge && <span className="ad-side-item-badge">{it.badge}</span>}
            </NavLink>
          ))}
        </div>
      ))}

      <div className="ad-side-spacer" />

      <div className="ad-side-foot">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: "var(--ad-fg-3)",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: "var(--ad-success)",
            }}
          />
          All systems operational
        </div>
        <a
          href="https://docs.authensee.com/"
          target="_blank"
          rel="noreferrer"
        >
          Docs ↗
        </a>
        <div style={{ marginTop: 4, fontSize: 10, color: "var(--ad-fg-4)" }}>
          v{__APP_VERSION__} · {import.meta.env.VITE_APP_ENV ?? "sandbox"}
        </div>
      </div>
    </aside>
  );
}
